import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import SmartImage from "@/components/SmartImage";
import FreshBadge from "@/components/FreshBadge";

/**
 * Compact JobCard — used on the Home page only.
 *
 * Shows title · company · location · 2 tags · logo.
 * No Dataleo angle, no summary. Full content lives on /jobs/{id}.
 */
const JobCardCompact = ({ item, idx = 0 }) => {
  const { t, lang } = useLang();
  const to = `/jobs/${item.id}`;
  return (
    <article
      data-testid={`job-card-compact-${idx}`}
      className="group bg-white border border-[#E5E7EB] hover:border-[#111827] transition-colors p-4 flex items-start gap-3"
    >
      <div className="w-12 h-12 shrink-0 border border-[#E5E7EB] bg-white grid place-items-center overflow-hidden">
        {item.company_logo ? (
          <SmartImage
            src={item.company_logo}
            type="job"
            alt={item.company || ""}
            fit="contain"
            className="max-w-[80%] max-h-[80%]"
          />
        ) : (
          <span className="font-display text-lg text-[#9ca3af]">
            {(item.company || "?").slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[10px] font-mono-dl uppercase tracking-wider">
          <span className="px-1.5 py-0.5 bg-[#111827] text-white">{item.work_mode}</span>
          {item.seniority && <span className="text-[#4B5563]">{item.seniority}</span>}
          <FreshBadge publishedAt={item.published_at} createdAt={item.created_at} lang={lang} />
          <span className="ml-auto text-[#9ca3af]">{item.published_at}</span>
        </div>
        <h3 className="font-display text-[15px] leading-snug mt-1">
          <Link to={to} className="text-[#111827] group-hover:text-[#00A878] transition-colors line-clamp-2">
            {item.title}
          </Link>
        </h3>
        <div className="text-[12px] text-[#4B5563] mt-1 truncate">
          {item.company}
          {item.location && (
            <>
              <span className="mx-1 text-[#9ca3af]">·</span>
              <span className="inline-flex items-center gap-0.5 align-baseline">
                <MapPin size={10} /> {item.location}
              </span>
            </>
          )}
        </div>
        {Array.isArray(item.tags) && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {item.tags.slice(0, 2).map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="text-[10px] font-mono-dl uppercase tracking-wider px-1.5 py-0.5 bg-[#F3F4F6] text-[#4B5563] border border-[#E5E7EB] hover:border-[#111827]"
              >
                {tag}
              </Link>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

export default JobCardCompact;
